import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { LoadingView } from '../../../../common/views/LoadingView';
import { useGetReleaseOverviewQuery } from '../../../../../redux/api/fymAPI';
import { Panel, PanelBody, PanelFoot, PanelHead } from '../../../components/primitives/Panel';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { visiblePosts } from './planModel';
import { planMessages } from './planMessages';
import { ReleasePostRow } from './ReleasePostRow';
import { useOpenReleaseCalendar } from './useOpenReleaseCalendar';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Plan tab; each part uses its own slice of it */
import styles from './Plan.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReleasePostsPanelProps {
  accountId: number;
  catalogItemUuid: string;
  title: string;
}

/**
 * "Posts of this release" (designs f1, f3): every post that uses an asset of the song or its
 * SmartLink (owner answer 6), newest first. Five rows show; "Show all" shows the rest. The panel
 * loads and fails by itself (design e2).
 */
const ReleasePostsPanelComponent: React.FC<ReleasePostsPanelProps> = ({ accountId, catalogItemUuid, title }) => {
  const intl = useIntl();
  const overview = useGetReleaseOverviewQuery({ accountId, catalogItemUuid });
  const [showAll, setShowAll] = useState(false);
  const openShowAll = useCallback(() => setShowAll(true), []);
  const openCalendar = useOpenReleaseCalendar(catalogItemUuid);
  const openCalendarRange = useCallback(() => openCalendar(), [openCalendar]);
  const posts = useMemo(() => overview.data?.posts ?? [], [overview.data]);
  const shown = useMemo(() => visiblePosts(posts, showAll), [posts, showAll]);
  const { refetch } = overview;
  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  let body: React.ReactNode;
  if (overview.isLoading) {
    body = <LoadingView />;
  } else if (overview.data === undefined) {
    body = (
      <div className={styles.panelError} role="alert">
        <span>{intl.formatMessage(planMessages.postsLoadFailed)}</span>
        <SMLButton onClick={retry}>{intl.formatMessage(planMessages.retry)}</SMLButton>
      </div>
    );
  } else if (posts.length === 0) {
    body = <p className={styles.empty}>{intl.formatMessage(planMessages.postsEmpty)}</p>;
  } else {
    body = (
      <table className={styles.posts}>
        <thead>
          <tr>
            <th>{intl.formatMessage(planMessages.colWhen)}</th>
            <th>{intl.formatMessage(planMessages.colPost)}</th>
            <th>{intl.formatMessage(planMessages.colChannel)}</th>
            <th>{intl.formatMessage(planMessages.colState)}</th>
            <th aria-hidden="true" />
          </tr>
        </thead>
        <tbody>
          {shown.map((post) => (
            <ReleasePostRow key={post.uuid} post={post} onOpenInCalendar={openCalendar} />
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <Panel as="section" aria-label={intl.formatMessage(planMessages.postsTitle)} data-testid="release-posts">
      <PanelHead
        title={intl.formatMessage(planMessages.postsTitle)}
        subtitle={intl.formatMessage(planMessages.postsSubtitle, { title })}
        right={<SMLButton onClick={openCalendarRange}>{intl.formatMessage(planMessages.openCalendar)}</SMLButton>}
      />
      <PanelBody flush>{body}</PanelBody>
      {posts.length > shown.length && (
        <PanelFoot className={styles.postsFoot}>
          <span>{intl.formatMessage(planMessages.postsFoot, { shown: shown.length, total: posts.length })}</span>
          <SMLButton onClick={openShowAll}>{intl.formatMessage(planMessages.showAll)}</SMLButton>
        </PanelFoot>
      )}
    </Panel>
  );
};

export const ReleasePostsPanel = TypedMemo(ReleasePostsPanelComponent);
